
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Message, WaitingMessage, MessageCategory } from '@/types/chat';

const WELCOME_MESSAGE: Message = {
  id: "welcome",
  text: "Ciao! Sono Gildy, il tuo assistente virtuale per l'oro e l'argento. Come posso aiutarti oggi?",
  isUser: false,
  timestamp: new Date()
};

type FeedbackType = 'positive' | 'negative' | null;

interface ChatState {
  // Messages
  messages: Message[];
  inputValue: string;
  isLoading: boolean;
  suggestionsVisible: boolean;
  
  // Message feedback
  messageFeedback: Record<string, FeedbackType>;
  
  // Waiting messages
  waitingMessages: WaitingMessage[];
  isShowingWaitingMessages: boolean;
  waitingMessageCategory: MessageCategory;
  
  // Widget state
  widgetOpen: boolean;
  
  // Hydration state
  hasHydrated: boolean;
  
  // Actions
  addMessage: (message: Message) => void;
  updateMessage: (id: string, updates: Partial<Message>) => void;
  clearMessages: () => void;
  setInputValue: (value: string) => void;
  setIsLoading: (loading: boolean) => void;
  setSuggestionsVisible: (visible: boolean) => void;
  toggleWidget: () => void;
  setWidgetOpen: (open: boolean) => void;
  setWaitingMessages: (messages: WaitingMessage[]) => void;
  setIsShowingWaitingMessages: (showing: boolean) => void;
  setWaitingMessageCategory: (category: MessageCategory) => void;
  setMessageFeedback: (messageId: string, type: 'positive' | 'negative') => void;
}

export const useChatStore = create<ChatState>()(
  persist(
    (set) => ({
      // Initial state
      messages: [WELCOME_MESSAGE],
      inputValue: '',
      isLoading: false,
      suggestionsVisible: true,
      messageFeedback: {},
      waitingMessages: [],
      isShowingWaitingMessages: false,
      waitingMessageCategory: 'GENERALE',
      widgetOpen: false,
      hasHydrated: false,
      
      // Actions
      addMessage: (message) => set((state) => ({
        messages: [...state.messages, message],
        suggestionsVisible: false
      })),
      
      updateMessage: (id, updates) => set((state) => ({
        messages: state.messages.map(msg => 
          msg.id === id ? { ...msg, ...updates } : msg
        )
      })),
      
      clearMessages: () => set({
        messages: [WELCOME_MESSAGE],
        suggestionsVisible: true,
        waitingMessages: [],
        isShowingWaitingMessages: false,
        messageFeedback: {}
      }),
      
      setInputValue: (value) => set({ inputValue: value }),
      setIsLoading: (loading) => set({ isLoading: loading }),
      setSuggestionsVisible: (visible) => set({ suggestionsVisible: visible }),
      toggleWidget: () => set((state) => ({ widgetOpen: !state.widgetOpen })),
      setWidgetOpen: (open) => set({ widgetOpen: open }),
      setWaitingMessages: (messages) => set({ waitingMessages: messages }),
      setIsShowingWaitingMessages: (showing) => set({ isShowingWaitingMessages: showing }),
      setWaitingMessageCategory: (category) => set({ waitingMessageCategory: category }),
      setMessageFeedback: (messageId, type) => set((state) => ({
        messageFeedback: { ...state.messageFeedback, [messageId]: type }
      }))
    }),
    {
      name: 'gildy-chat',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        // Persist only messages (limit to last 50) and feedback
        messages: state.messages.slice(-50),
        messageFeedback: state.messageFeedback,
        widgetOpen: state.widgetOpen
      }),
      // Rehydrate timestamps as Date objects and disable streaming animations
      onRehydrateStorage: () => (state) => {
        if (state?.messages) {
          state.messages = state.messages.map(msg => ({
            ...msg,
            timestamp: new Date(msg.timestamp),
            isStreaming: false // Disable typewriter on reload
          }));
        }
        // Signal that hydration is complete
        if (state) {
          state.hasHydrated = true;
        }
      }
    }
  )
);
